// src/stores/userStore.js
import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { apiClient, resolveApiErrorMessage, withLoadingAndError } from '../utils/api';
import { classifyAuth0SessionError } from '../utils/auth0Error';
import { translateForLocale } from '../i18n';
import {
  fetchBootstrappedSessionProfile,
  PROFILE_BOOTSTRAP_RESULT,
} from '../auth/sessionOrchestrator';
import useSessionOrchestratorStore from '../auth/sessionOrchestrator/store';
import { mapSessionExpiryReasonToBlockingReason } from '../auth/sessionOrchestrator/recoveryModel';
import {
  applyLocaleToDocument,
  getInitialLocale,
  resolveLocale,
  writeStoredLocale,
} from '../utils/locale';
import { getLocationReturnTo } from '../utils/sessionRecovery';
import {
  clearCachedUserSession,
  persistHydratedUserData,
  userSessionStorage,
} from './userSessionPersistence';

/**
 * Ce store gère :
 * - la session applicative (token access, userData)
 * - la récupération/auto-création du profil côté API (/users/me)
 */

/**
 * Base URL pour les redirections (logout)
 */
const BASE_URL = (
  import.meta.env?.VITE_BASE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'https://riveltime.app')
).replace(/\/+$/, '');

// Verrou anti-spam sur fetchUser pour éviter de bombarder /users/me
let fetchUserLock = null;
let fetchUserCooldownUntil = 0;
const initialPreferredLocale = getInitialLocale();
const tUserStore = (state, key, values = {}, fallback = '') => {
  const locale = resolveLocale(
    state?.preferredLocale || state?.userData?.preferredLocale || initialPreferredLocale
  );
  return translateForLocale(locale, key, values, fallback);
};

const clearLocalAccountScopedState = () => {
  if (typeof localStorage === 'undefined') return;
  const exactKeys = [
    'cart-storage',
    'delivery-address-storage',
    'order-store',
  ];

  for (const key of exactKeys) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore storage failures
    }
  }

  try {
    const keysToRemove = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith('swr:')) keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore storage failures
  }
};

// Applique la locale au document dès l'import du store (avant le rendu React).
applyLocaleToDocument(initialPreferredLocale);

const useUserStore = create(
  devtools(
    persist(
      (set, get) => ({
        // ---- STATE ---------------------------------------------------------
        token: null,
        userData: null,
        preferredLocale: initialPreferredLocale,
        loadingUser: true,
        auth0User: null,           // objet Auth0 (sub, email, etc.)
        getTokenSilently: null,    // fn injectée (Auth0.getAccessTokenSilently)
        identityLinkConflict: null,

        // ---- MUTATIONS ATOMIQUES ------------------------------------------
        setToken: (t) => {
          if (!t) return;
          set({
            token: t,
          });
        },

        setUserData: (u) => {
          if (!u) return;
          const persisted = persistHydratedUserData({
            userData: u,
            preferredLocale: get().preferredLocale,
            fallbackLocale: initialPreferredLocale,
          });
          if (!persisted) return;
          set({
            userData: persisted.userData,
            preferredLocale: persisted.preferredLocale,
            identityLinkConflict: null,
          });
        },

        setPreferredLocale: (locale) => {
          const resolvedLocale = resolveLocale(locale, get().preferredLocale || initialPreferredLocale);
          writeStoredLocale(resolvedLocale);
          applyLocaleToDocument(resolvedLocale);
          set((state) => ({
            preferredLocale: resolvedLocale,
            userData: state.userData
              ? { ...state.userData, preferredLocale: resolvedLocale }
              : state.userData,
          }));
        },

        setIdentityLinkConflict: (conflict) => {
          set({
            identityLinkConflict:
              conflict && typeof conflict === 'object'
                ? {
                    ...conflict,
                    canLinkIdentities: conflict?.canLinkIdentities !== false,
                  }
                : null,
          });
        },

        clearIdentityLinkConflict: () => {
          set({ identityLinkConflict: null });
        },

        markSessionExpired: ({ reason = 'session_expired', context = 'unknown' } = {}) => {
          const sessionState = useSessionOrchestratorStore.getState();
          if (sessionState?.phase === 'recovery_required') return;
          sessionState.requestRecovery?.({
            blockingReason: mapSessionExpiryReasonToBlockingReason(reason),
            targetPath: getLocationReturnTo(
              typeof window !== 'undefined' ? window.location : null
            ),
            recoveryContext: context,
          });
          clearCachedUserSession();
          set({
            token: null,
            userData: null,
            loadingUser: false,
            auth0User: null,
            getTokenSilently: null,
            identityLinkConflict: null,
          });
        },

        handleAuth0TokenError: (err, context = 'unknown') => {
          const reason = classifyAuth0SessionError(err);
          if (!reason) return false;
          get().markSessionExpired({ reason, context });
          return true;
        },

        // ---- FETCH USER (online) ------------------------------------------
        /**
         * Récupère le profil côté API.
         * - 1er essai sur GET /users/me
         * - si 410 → re-essaye GET /users/me?createIfMissing=1 (auto-upsert contrôlé)
         */
        fetchUser: async ({ silent = false } = {}) => {
          const now = Date.now();
          if (fetchUserCooldownUntil && now < fetchUserCooldownUntil) {
            if (!silent) set({ loadingUser: false });
            return;
          }

          if (fetchUserLock) return fetchUserLock;

          const getTS = get().getTokenSilently;
          if (!getTS) {
            // Pas encore d’intégration Auth0 (ex: première frame) → on sort proprement
            if (!silent) set({ loadingUser: false });
            return;
          }

          fetchUserLock = withLoadingAndError(set, async () => {
            if (!silent) set({ loadingUser: true });

            // Access token Auth0 (tolérant à l'absence de RT après logout)
            let accessToken;
            try {
              accessToken = await getTS({
                audience: import.meta.env.VITE_AUTH0_AUDIENCE,
              });
            } catch (e) {
              const handled = get().handleAuth0TokenError?.(e, 'userStore:fetchUser');
              if (handled) return;
              throw e;
            }
            if (!accessToken) {
              set({
                token: null,
                userData: null,
                loadingUser: false,
                identityLinkConflict: null,
              });
              return;
            }

            get().setToken(accessToken);

            const bootstrapResult = await fetchBootstrappedSessionProfile({
              apiClient,
              accessToken,
              auth0User: get().auth0User,
              preferredLocale: get().preferredLocale,
              initialPreferredLocale,
              resolveLocale,
              resolveApiErrorMessage,
              unableToFetchProfileMessage: tUserStore(get(), 'userStore.unableToFetchProfile', {}),
              emailAlreadyLinkedMessage: tUserStore(
                get(),
                'userStore.emailAlreadyLinkedOtherAccount',
                {}
              ),
            });

            if (bootstrapResult?.kind === PROFILE_BOOTSTRAP_RESULT.identityLinkConflict) {
              set({
                identityLinkConflict: bootstrapResult.conflict,
              });
              return;
            }

            if (bootstrapResult?.kind === PROFILE_BOOTSTRAP_RESULT.resolved && bootstrapResult.user) {
              get().setUserData(bootstrapResult.user);
              return;
            }

            throw new Error(tUserStore(get(), 'userStore.unableToFetchProfile', {}));
          })
            .catch((err) => {
              const status = err?.response?.status;
              if (status === 429) {
                // Cooldown de 15s pour éviter les boucles et respecter le rate limit
                fetchUserCooldownUntil = Date.now() + 15_000;
                console.warn('[userStore] fetchUser throttle après 429 — pause 15s');
                return;
              }
              throw err;
            })
            .finally(() => {
              fetchUserLock = null;
              if (!silent) set({ loadingUser: false });
            });

          return fetchUserLock;
        },

        // ---- IDENTITY LINKING --------------------------------------------
        startIdentityLinkFlow: async ({ targetProvider } = {}) => {
          const state = get();
          const getTS = state.getTokenSilently;
          if (!getTS) {
            throw new Error(
              tUserStore(state, 'userStore.invalidSessionReconnect', {})
            );
          }

          let accessToken = state.token;
          if (!accessToken) {
            accessToken = await getTS({
              audience: import.meta.env.VITE_AUTH0_AUDIENCE,
            });
          }
          if (!accessToken) {
            throw new Error(
              tUserStore(state, 'userStore.invalidSessionReconnect', {})
            );
          }

          const response = await apiClient.post(
            '/account/link-identities/start',
            targetProvider ? { targetProvider } : {},
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );

          return response?.data || {};
        },

        confirmIdentityLinkFlow: async ({ linkSessionId } = {}) => {
          const state = get();
          const getTS = state.getTokenSilently;
          if (!getTS || !linkSessionId) {
            throw new Error(
              tUserStore(state, 'userStore.unableToFetchProfile', {})
            );
          }

          const accessToken =
            state.token ||
            (await getTS({
              audience: import.meta.env.VITE_AUTH0_AUDIENCE,
            }));

          // Prefer a fresh proof token, but gracefully fallback to current session token
          // when silent re-auth is not possible in the browser context.
          let reauthProof = null;
          try {
            reauthProof = await getTS({
              audience: import.meta.env.VITE_AUTH0_AUDIENCE,
              cacheMode: 'off',
            });
          } catch {
            reauthProof = accessToken;
          }

          if (!accessToken || !reauthProof) {
            throw new Error(
              tUserStore(state, 'userStore.invalidSessionReconnect', {})
            );
          }

          const response = await apiClient.post(
            '/account/link-identities/confirm',
            {
              linkSessionId,
              reauthProof,
            },
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );

          set({ identityLinkConflict: null });
          return response?.data || {};
        },

        // ---- DELETE ACCOUNT -----------------------------------------------
        // Renvoie:
        //  - { ok: true }                     → suppression réussie
        //  - { ok: false, code, details? }    → suppression refusée (ex: ACTIVE_ORDERS)
        //  - { ok: false }                    → autre erreur
        deleteAccount: async () => {
          const { token, userData } = get();
          if (!token || !userData) {
            return {
              ok: false,
              code: 'SESSION_INVALID',
              message: tUserStore(get(), 'userStore.invalidSessionReconnect', {}),
            };
          }

          try {
            // on n'utilise pas withLoadingAndError ici pour pouvoir traiter le 423 proprement
            const res = await apiClient.delete('/account/delete/me', {
              // on capte les 4xx pour décider nous-même
              validateStatus: (s) => (s >= 200 && s < 500),
            });

            if (res.status === 200 || res.status === 204) {
              clearCachedUserSession();
              clearLocalAccountScopedState();
              useSessionOrchestratorStore.getState().resetSessionOrchestrator?.();
              set({
                token: null,
                userData: null,
                loadingUser: false,
                auth0User: null,
                getTokenSilently: null,
                identityLinkConflict: null,
              });
              return { ok: true };
            }

            if (res.status === 423) {
              // Blocage métier: commandes actives
              const details = res?.data?.details || {};
              return { ok: false, code: 'ACTIVE_ORDERS', details };
            }

            // autres erreurs lisibles
            const msg = resolveApiErrorMessage(
              { response: { data: res?.data || {}, status: res?.status } },
              {
                fallbackKey: 'userStore.deleteAccountFailed',
                fallbackMessage: tUserStore(get(), 'userStore.deleteAccountFailed', {}),
              }
            );
            set({ error: msg });
            return { ok: false };
          } catch (err) {
            // erreur réseau / inconnue
            const msg = resolveApiErrorMessage(err, {
              fallbackKey: 'userStore.unexpectedError',
              fallbackMessage: tUserStore(get(), 'userStore.unexpectedError', {}),
            });
            set({ error: msg });
            return { ok: false };
          }
        },

        // ---- LOGOUT SÉCURISÉ ----------------------------------------------
        logoutSafe: (logoutFn) => {
          clearCachedUserSession();
          clearLocalAccountScopedState();
          useSessionOrchestratorStore.getState().resetSessionOrchestrator?.();
          set({
            token: null,
            userData: null,
            loadingUser: false,
            auth0User: null,
            getTokenSilently: null,
            identityLinkConflict: null,
          });

          // Utilise logoutParams.returnTo (recommandé par @auth0/auth0-react)
          if (typeof logoutFn === 'function') {
            logoutFn({
              logoutParams: { returnTo: BASE_URL },
            });
          }
        },        

        // ---- HELPERS ROLES -------------------------------------------------
        isAdmin: () => get().userData?.role === 'admin',
        hasRole: (role) => {
          const r = get().userData?.role;
          return r === role || r === 'admin';
        },
      }),
      {
        name: 'user-store',
        storage: createJSONStorage(() => userSessionStorage),
        // On ne persiste que ces clés (token toujours en mémoire pour éviter de le stocker en clair)
        partialize: (state) => ({
          userData: state.userData,
          auth0User: state.auth0User,
          preferredLocale: state.preferredLocale,
        }),
        merge: (persistedState, currentState) => {
          const merged = {
            ...currentState,
            ...persistedState,
            token: null, // on ne réhydrate jamais un access token stocké
          };
          const localeFromPersisted = resolveLocale(
            merged?.userData?.preferredLocale ||
            merged?.preferredLocale ||
            currentState.preferredLocale ||
            initialPreferredLocale
          );
          merged.preferredLocale = localeFromPersisted;
          if (merged?.userData) {
            merged.userData = {
              ...merged.userData,
              preferredLocale: localeFromPersisted,
            };
          }
          writeStoredLocale(localeFromPersisted);
          applyLocaleToDocument(localeFromPersisted);
          return merged;
        },
      }
    )
  )
);

export default useUserStore;
